import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { Programme } from '../lib/types';
import { Card, EmptyState, ErrorNote, PageHead, Spinner, Tag } from '../components/ui';

const ROUTING_LABEL: Record<string, string> = {
  DIRECT_ASSIGNMENT: 'Direct assignment',
  RULE_BASED: 'Rule-based matching',
  AUCTION: 'Auction to panel',
};

const SETTLEMENT_LABEL: Record<string, string> = {
  DIRECT_TO_LENDER: 'Direct to lender',
  PLATFORM_ESCROW: 'Platform escrow',
};

export function ProgrammesPage() {
  const query = useQuery({
    queryKey: ['programmes'],
    queryFn: () => api.get<Programme[]>('/programmes'),
  });

  if (query.isLoading) return <Spinner label="Loading programmes" />;
  if (query.error) return <ErrorNote error={query.error} />;

  const programmes = query.data ?? [];

  return (
    <>
      <PageHead
        crumb="Configuration"
        title="Programmes"
        sub="A deal snapshots these rates when it is routed, so editing a programme never rewrites terms already agreed."
      />

      <Card padded={false}>
        {programmes.length === 0 ? (
          <EmptyState title="No programmes configured" />
        ) : (
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Programme</th>
                  <th>Routing</th>
                  <th>Settlement</th>
                  <th className="r">Advance</th>
                  <th className="r">Fee</th>
                  <th className="r">PO advance</th>
                  <th>Recourse</th>
                  <th className="r">Min score</th>
                  <th className="r">Deals</th>
                </tr>
              </thead>
              <tbody>
                {programmes.map((p) => (
                  <tr key={p.id} style={{ cursor: 'default' }}>
                    <td>
                      <span className="cell-t1">{p.name}</span>
                      <span className="cell-t2" style={{ display: 'block' }}>
                        {p.code}
                        {p.defaultLender ? ` · ${p.defaultLender.name}` : ''}
                      </span>
                    </td>
                    <td className="cell-t2">{ROUTING_LABEL[p.routingMode] ?? p.routingMode}</td>
                    <td className="cell-t2">
                      {SETTLEMENT_LABEL[p.settlementMode] ?? p.settlementMode}
                    </td>
                    <td className="r num">{p.advancePct ?? `${(p.advanceBps / 100).toFixed(2)}%`}</td>
                    <td className="r num">{p.feePct ?? `${(p.feeBps / 100).toFixed(2)}%`}</td>
                    <td className="r num">
                      {p.poAdvanceBps === null ? '—' : `${(p.poAdvanceBps / 100).toFixed(0)}%`}
                    </td>
                    <td>
                      <Tag tone={p.recourse ? 'warning' : 'positive'}>
                        {p.recourse ? 'With recourse' : 'Non-recourse'}
                      </Tag>
                    </td>
                    <td className="r num">{p.minScore}</td>
                    <td className="r num">{p.dealCount ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
